import { editName as questionEditName } from "@ext/markdown/elements/question/consts";
import { type Editor, findParentNodeClosestToPos } from "@tiptap/core";
import type { MarkType } from "@tiptap/pm/model";
import { type EditorState, Plugin, PluginKey, type Transaction } from "@tiptap/pm/state";
import { AttrStep, Mapping } from "@tiptap/pm/transform";

export default function QuestionStateUpdater(this: { name: string; editor: Editor; type: MarkType }) {
	const updateQuestionState = (
		tr: Transaction,
		state: EditorState,
		toState: Mapping,
		appendedTr: Transaction | null,
	): Transaction | null => {
		if (!tr.docChanged) return appendedTr;
		let newTr = appendedTr;

		tr.steps.forEach((step, index) => {
			if (step instanceof AttrStep && step.attr === "correct") {
				const mapping = tr.mapping.slice(index + 1);
				mapping.appendMapping(toState);
				const { pos, deleted } = mapping.mapResult(step.pos, 1);
				if (deleted) return;

				const node = state.doc.nodeAt(pos);
				const oldNode = tr.docs[index].nodeAt(step.pos);
				const nodeId = node.attrs.answerId || node.attrs.id;
				if (!nodeId) return;

				const oldValue = oldNode?.attrs[step.attr];
				const newValue = node.attrs[step.attr];

				if (oldValue === newValue) return;

				const questionData = findParentNodeClosestToPos(
					state.doc.resolve(pos),
					(node) => node.type.name === questionEditName,
				);
				if (!questionData || questionData.node.attrs.id !== node.attrs.questionId) return;

				const questionNode = questionData.node;
				const isOneQuestionType = questionNode.attrs.type === "one";

				let hasNullAnswers = false;
				let isAllFalse = true;
				questionNode.content.forEach((answerNode) => {
					if (answerNode.attrs.correct === true) {
						isAllFalse = false;
						return;
					}

					if (answerNode.attrs.correct === null) {
						hasNullAnswers = true;
						return;
					}
				});

				questionNode.content.forEach((answerNode, offset) => {
					if (answerNode.attrs.answerId !== nodeId) {
						if (!isAllFalse && isOneQuestionType && newValue === true) {
							if (hasNullAnswers || answerNode.attrs.correct === true) {
								newTr = newTr || state.tr;
								newTr = newTr.setNodeAttribute(questionData.start + offset, "correct", false);
							}
						}

						if (hasNullAnswers && !isOneQuestionType && newValue === true) {
							newTr = newTr || state.tr;
							newTr = newTr.setNodeAttribute(questionData.start + offset, "correct", false);
						}
					}

					if (isAllFalse) {
						newTr = newTr || state.tr;
						newTr = newTr.setNodeAttribute(questionData.start + offset, "correct", null);
					}
				});
			}
		});

		return newTr;
	};

	return new Plugin({
		key: new PluginKey("questionStateUpdater$"),
		// eslint-disable-next-line @typescript-eslint/no-unused-vars
		appendTransaction(transactions, _, newState) {
			let newTr: Transaction | null = null;

			transactions.forEach((tr, index) => {
				const toNewState = new Mapping(transactions.slice(index + 1).flatMap((next) => next.mapping.maps));
				newTr = updateQuestionState(tr, newState, toNewState, newTr);
			});

			return newTr;
		},
	});
}
